/**
 * Phase 5 · Billing & payments: bill preparation (discounts with approval
 * limits, service charge, tips), full split payment (single / equal / by
 * amount), multi-tender settlement, refunds, thermal & A4 receipts, cash
 * drawer shifts and the Z report.
 */
import { useCallback, useEffect, useState } from 'react';
import { Printer, Undo2 } from 'lucide-react';
import { http, ApiError } from '../api/client';
import { useUi } from '../store/ui';
import { money } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Badge, StatusBadge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input, Select } from '../components/ui/Input';

const METHODS = ['cash', 'card', 'mobile_wallet', 'upi', 'bank_transfer', 'credit_note', 'gift_card', 'house_account', 'voucher', 'crypto', 'other'];

interface Order { id: string; order_number: string; order_type: string; status: string; total: number; table_label: string | null }
interface OrderItem { id: string; name_snapshot: string; quantity: number; line_total: number; status: string }
interface Split { id: string; guest_label: string; total: number; paid: number; is_paid: boolean }
interface Tender { method: string; amount: string }

export function BillingPage() {
  const toast = useUi((s) => s.toast);
  const [queue, setQueue] = useState<Order[]>([]);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [bill, setBill] = useState<{ id: string; total: number; splits: Split[] } | null>(null);
  const [loading, setLoading] = useState(true);

  const [discountPct, setDiscountPct] = useState('');
  const [servicePct, setServicePct] = useState('');
  const [tip, setTip] = useState('');
  const [mode, setMode] = useState<'single' | 'equal' | 'amount'>('single');
  const [guests, setGuests] = useState('2');
  const [splits, setSplits] = useState<Split[]>([]);
  const [tenders, setTenders] = useState<Tender[]>([{ method: 'cash', amount: '' }]);

  const [receipt, setReceipt] = useState<string | null>(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [zOpen, setZOpen] = useState(false);

  const loadQueue = useCallback(async () => {
    try {
      const r = await http.get<{ orders: Order[] }>('/orders', { status: 'open,sent,preparing,ready' });
      setQueue(r.orders);
    } catch (e) {
      toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { void loadQueue(); }, [loadQueue]);

  const select = async (o: Order) => {
    setOrderId(o.id);
    try {
      const d = await http.get<{ order: Order; items: OrderItem[]; bill: (Record<string, unknown> & { splits?: Split[] }) | null }>(`/orders/${o.id}`);
      setOrder(d.order); setItems(d.items);
      setBill(d.bill ? { id: String(d.bill.id), total: Number(d.bill.total ?? o.total), splits: (d.bill.splits ?? []) as Split[] } : null);
      setTenders([{ method: 'cash', amount: String((Number(d.order.total) / 100).toFixed(2)) }]);
    } catch (e) {
      toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  const prepare = async () => {
    if (!orderId) return;
    try {
      await http.post(`/billing/orders/${orderId}/prepare`, {
        discountPct: discountPct ? parseFloat(discountPct) : undefined,
        serviceChargePct: servicePct ? parseFloat(servicePct) : 0,
        tip: tip ? Math.round(parseFloat(tip) * 100) : 0,
      });
      await select(queue.find((q) => q.id === orderId) ?? (order as Order));
      toast({ kind: 'success', title: 'Bill recalculated' });
    } catch (e) {
      toast({ kind: 'error', title: 'Prepare failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  const preview = async () => {
    if (!orderId) return;
    try {
      const body = mode === 'equal' ? { mode, guests: parseInt(guests, 10) } : { mode, guests: parseInt(guests, 10) };
      const r = await http.post<{ splits: Split[] }>(`/billing/orders/${orderId}/split-preview`, body);
      setSplits(r.splits);
    } catch (e) {
      toast({ kind: 'error', title: 'Preview failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  const settle = async () => {
    if (!orderId) return;
    try {
      const payments = tenders.filter((t) => parseFloat(t.amount) > 0).map((t) => ({ method: t.method, amount: Math.round(parseFloat(t.amount) * 100) }));
      const body: Record<string, unknown> = { mode, payments };
      if (mode === 'equal') body.guests = parseInt(guests, 10);
      if (mode === 'amount') body.amounts = splits.map((s, i) => ({ guestLabel: s.guest_label, amount: Math.round(parseFloat(amountFor(i)) * 100) }));
      await http.post(`/billing/orders/${orderId}/settle`, body);
      toast({ kind: 'success', title: `Order #${order?.order_number} settled` });
      setOrderId(null); setOrder(null); setSplits([]);
      await loadQueue();
    } catch (e) {
      toast({ kind: 'error', title: 'Settle failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  const [splitAmounts, setSplitAmounts] = useState<Record<number, string>>({});
  const amountFor = (i: number) => splitAmounts[i] ?? (splits[i] ? String(Number(splits[i].total) / 100) : '0');

  const showReceipt = async (format: 'thermal58' | 'thermal80' | 'a4') => {
    if (!orderId) return;
    try {
      const r = await http.get<{ receipt: unknown }>(`/billing/orders/${orderId}/receipt`, { format });
      setReceipt(typeof r.receipt === 'string' ? r.receipt : JSON.stringify(r.receipt, null, 2));
    } catch (e) {
      toast({ kind: 'error', title: 'Receipt failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  const outstanding = (bill?.total ?? order?.total ?? 0);

  return (
    <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
      {/* Queue */}
      <section className="space-y-2">
        <header className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-ink">Billing</h1>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => setZOpen(true)}>Z report</Button>
            <Button size="sm" variant="secondary" onClick={() => setShiftOpen(true)}>Shift</Button>
          </div>
        </header>
        {loading ? <p className="text-sm text-ink-muted">Loading…</p> :
          queue.map((o) => (
            <button key={o.id} onClick={() => void select(o)}
              className={`min-h-touch w-full rounded-xl border p-3 text-start ${orderId === o.id ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20' : 'border-line bg-surface-0 hover:border-brand-400'}`}>
              <span className="flex items-center justify-between text-sm">
                <span className="font-medium text-ink">#{o.order_number} {o.table_label ? `· ${o.table_label}` : ''}</span>
                <span className="font-semibold text-ink">{money(o.total)}</span>
              </span>
              <span className="mt-1 flex items-center justify-between text-xs text-ink-muted">
                <span>{o.order_type.replace('_', ' ')}</span><StatusBadge status={o.status} />
              </span>
            </button>
          ))}
        {!loading && !queue.length && <p className="text-sm text-ink-muted">No unpaid orders right now.</p>}
      </section>

      <section className="rounded-xl border border-line bg-surface-0 p-4">
        {order ? (
          <div className="space-y-4">
            <header className="flex items-center justify-between">
              <h2 className="font-semibold text-ink">Order #{order.order_number}</h2>
              <span className="text-lg font-semibold text-ink">{money(outstanding)}</span>
            </header>

            <div className="space-y-1 text-sm">
              {items.map((i) => (
                <div key={i.id} className="flex justify-between text-ink"><span>{i.quantity}× {i.name_snapshot}</span><span>{money(i.line_total)}</span></div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Input label="Discount %" type="number" min="0" max="100" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} />
              <Input label="Service %" type="number" min="0" max="100" value={servicePct} onChange={(e) => setServicePct(e.target.value)} />
              <Input label="Tip (£)" type="number" min="0" step="0.01" value={tip} onChange={(e) => setTip(e.target.value)} />
            </div>
            <Button variant="secondary" onClick={() => void prepare()}>Apply & recalculate</Button>

            <div className="space-y-2 border-t border-line pt-3">
              <div className="flex items-end gap-2">
                <Select label="Split mode" value={mode} onChange={(e) => setMode(e.target.value as never)}>
                  <option value="single">Single bill</option>
                  <option value="equal">Split equally</option>
                  <option value="amount">Split by amount</option>
                </Select>
                {mode !== 'single' && <Input label="Guests" type="number" min="1" max="40" value={guests} onChange={(e) => setGuests(e.target.value)} className="w-24" />}
                {mode !== 'single' && <Button variant="secondary" onClick={() => void preview()}>Preview</Button>}
              </div>
              {splits.length > 0 && (
                <div className="space-y-1 text-sm">
                  {splits.map((s, i) => (
                    <div key={s.id} className="flex items-center justify-between gap-2">
                      <span className="text-ink">{s.guest_label}</span>
                      {mode === 'amount'
                        ? <Input aria-label={`Amount for ${s.guest_label}`} type="number" step="0.01" className="w-28" value={amountFor(i)} onChange={(e) => setSplitAmounts((m) => ({ ...m, [i]: e.target.value }))} />
                        : <span className="text-ink">{money(s.total)}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2 border-t border-line pt-3">
              <p className="text-sm font-semibold text-ink">Payments</p>
              {tenders.map((t, i) => (
                <div key={i} className="grid grid-cols-[1fr_8rem_2.5rem] gap-2">
                  <Select aria-label="Method" value={t.method} onChange={(e) => setTenders((ts) => ts.map((x, j) => j === i ? { ...x, method: e.target.value } : x))}>
                    {METHODS.map((m) => <option key={m}>{m}</option>)}
                  </Select>
                  <Input aria-label="Amount" type="number" step="0.01" min="0" value={t.amount} onChange={(e) => setTenders((ts) => ts.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} />
                  <Button variant="ghost" aria-label="Remove tender" onClick={() => setTenders((ts) => ts.filter((_, j) => j !== i))}>✕</Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setTenders((ts) => [...ts, { method: 'card', amount: '' }])}>+ tender</Button>
                <span className="ms-auto text-sm text-ink-muted">tendered {money(tenders.reduce((a, t) => a + Math.round(parseFloat(t.amount || '0') * 100), 0))}</span>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button onClick={() => void settle()}>Settle payment</Button>
                <Button variant="secondary" onClick={() => void showReceipt('thermal80')}><Printer size={15} /> 80mm</Button>
                <Button variant="secondary" onClick={() => void showReceipt('thermal58')}><Printer size={15} /> 58mm</Button>
                <Button variant="secondary" onClick={() => void showReceipt('a4')}><Printer size={15} /> A4</Button>
                <Button variant="danger" onClick={() => setRefundOpen(true)}><Undo2 size={15} /> Refund</Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full min-h-[16rem] items-center justify-center">
            <p className="text-sm text-ink-muted">Select an order on the left to prepare its bill.</p>
          </div>
        )}
      </section>

      {receipt && (
        <Modal open onClose={() => setReceipt(null)} title="Receipt preview" size="lg"
          footer={<Button variant="secondary" onClick={() => setReceipt(null)}>Close</Button>}>
          <pre className="max-h-96 overflow-auto rounded-lg bg-surface-1 p-3 font-mono text-xs text-ink">{receipt}</pre>
        </Modal>
      )}
      {refundOpen && orderId && (
        <RefundModal orderId={orderId} onClose={() => setRefundOpen(false)} onDone={async () => { setRefundOpen(false); await loadQueue(); }} />
      )}
      {shiftOpen && <ShiftModal onClose={() => setShiftOpen(false)} />}
      {zOpen && <ZModal onClose={() => setZOpen(false)} />}
    </div>
  );
}

function RefundModal({ orderId, onClose, onDone }: { orderId: string; onClose: () => void; onDone: () => Promise<void> }) {
  const toast = useUi((s) => s.toast);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await http.post(`/billing/orders/${orderId}/refund`, {
        reason, method, amount: amount ? Math.round(parseFloat(amount) * 100) : undefined,
      });
      toast({ kind: 'success', title: 'Refund processed' });
      await onDone();
    } catch (e) {
      toast({ kind: 'error', title: 'Refund failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Refund"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button variant="danger" loading={busy} disabled={reason.trim().length < 3} onClick={() => void run()}>Refund</Button></>}>
      <div className="grid gap-3">
        <Input label="Reason (audited)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Amount (£, empty = full)" type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <Select label="Method" value={method} onChange={(e) => setMethod(e.target.value)}>{METHODS.map((m) => <option key={m}>{m}</option>)}</Select>
        </div>
      </div>
    </Modal>
  );
}

function ShiftModal({ onClose }: { onClose: () => void }) {
  const toast = useUi((s) => s.toast);
  const [opening, setOpening] = useState('50');
  const [openShift, setOpenShift] = useState<Record<string, unknown> | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    void http.get<{ shift: Record<string, unknown> | null }>('/billing/shifts/current').then((r) => { setOpenShift(r.shift); setChecked(true); }).catch(() => setChecked(true));
  }, []);

  const open = async () => {
    try {
      await http.post('/billing/shifts/open', { openingFloat: Math.round(parseFloat(opening || '0') * 100) });
      toast({ kind: 'success', title: 'Shift opened' }); onClose();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
  };
  const close = async () => {
    if (!openShift) return;
    try {
      await http.post(`/billing/shifts/${String(openShift.id)}/close`, {});
      toast({ kind: 'success', title: 'Shift closed' }); onClose();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
  };

  return (
    <Modal open onClose={onClose} title="Cash drawer shift"
      footer={<>
        <Button variant="secondary" onClick={onClose}>Close</Button>
        {openShift ? <Button variant="danger" onClick={() => void close()}>Close shift</Button>
          : <Button onClick={() => void open()}>Open shift</Button>}
      </>}>
      {!checked ? <p className="text-sm text-ink-muted">Checking…</p> : openShift ? (
        <div className="space-y-1 text-sm text-ink">
          <p>Shift <Badge tone="success">open</Badge> since {String(openShift.started_at)}</p>
          <p>Orders {String(openShift.order_count)} · sales {money(Number(openShift.total_sales ?? 0))}</p>
          <p>Cash {money(Number(openShift.cash_sales ?? 0))} · card {money(Number(openShift.card_sales ?? 0))} · refunds {money(Number(openShift.refunds ?? 0))}</p>
        </div>
      ) : (
        <Input label="Opening float (£)" type="number" step="0.01" min="0" value={opening} onChange={(e) => setOpening(e.target.value)} />
      )}
    </Modal>
  );
}

function ZModal({ onClose }: { onClose: () => void }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    void http.get<Record<string, unknown>>('/billing/z-report', { date }).then(setReport).catch(() => setReport({}));
  }, [date]);
  return (
    <Modal open onClose={onClose} title="Z report" size="lg" footer={<Button variant="secondary" onClick={onClose}>Close</Button>}>
      <Input label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      {report && (
        <pre className="mt-3 max-h-96 overflow-auto rounded-lg bg-surface-1 p-3 font-mono text-xs text-ink">{JSON.stringify(report, null, 2)}</pre>
      )}
    </Modal>
  );
}
